import { ThunkAction } from "redux-thunk";

import {
  receiveTemplateSchedule,
  SchedulingThunkResult,
} from "actions/scheduling";
import {
  scheduleToTemplate,
  templateToSchedule,
  emptyScheduleOfShifts,
} from "helpers/transforms/templates";
import {
  getSavedTemplates,
  postTemplate,
  requestDeleteTemplate,
  updateTemplate,
} from "interfaces/template";
import {
  IRootStateProps,
  ISchedulingState,
  TemplateApiRequests,
} from "types/reducers";
import { RequestStatus } from "types/requests";
import { ITemplate } from "types/templates";
import { mergePeopleIntoSchedule } from "helpers/transforms/schedule";
import { trackEvent } from "helpers/analytics";

enum TemplatesTypeKeys {
  RECEIVE_TEMPLATES = "TEMPLATES/RECEIVE_TEMPLATES",
  REMOVE_TEMPLATE = "TEMPLATES/REMOVE_TEMPLATE",
  SET_REQUEST_STATUS = "TEMPLATES/SET_REQUEST_STATUS",
}

type TemplatesActionTypes =
  | IReceiveTemplatesAction
  | IRemoveTemplateAction
  | ISetRequestStatusAction<TemplateApiRequests>;

type TemplatesThunkResult<R> = ThunkAction<
  R,
  IRootStateProps,
  undefined,
  TemplatesActionTypes
>;

interface IReceiveTemplatesAction {
  type: TemplatesTypeKeys.RECEIVE_TEMPLATES;
  templates: ITemplate[];
}
function receiveTemplates(templates: ITemplate[]): IReceiveTemplatesAction {
  return {
    type: TemplatesTypeKeys.RECEIVE_TEMPLATES,
    templates,
  };
}

interface IRemoveTemplateAction {
  type: TemplatesTypeKeys.REMOVE_TEMPLATE;
  uuid: string;
}
function removeTemplate(uuid: string): IRemoveTemplateAction {
  return {
    type: TemplatesTypeKeys.REMOVE_TEMPLATE,
    uuid,
  };
}

function fetchTemplates(permalink: string): TemplatesThunkResult<void> {
  return (dispatch): Promise<void> => {
    dispatch(setGetTemplatesRequestStatus(RequestStatus.REQUESTING));

    return getSavedTemplates(permalink)
      .then((templates: ITemplate[]) => {
        dispatch(receiveTemplates(templates));
      })
      .catch(() => {
        dispatch(setGetTemplatesRequestStatus(RequestStatus.FAILURE));
      });
  };
}

function saveTemplate(
  permalink: string,
  name: string,
  uuid?: string
): TemplatesThunkResult<void> {
  return (dispatch, getState): Promise<void> => {
    dispatch(setPostTemplateRequestStatus(RequestStatus.REQUESTING));
    const currentScheduleState: ISchedulingState = getState().scheduling;
    const currentScheduleAsTemplate = scheduleToTemplate(
      currentScheduleState.schedule,
      name,
      uuid
    );
    // Either post or update, depending if existing uuid was given
    const apiAction = uuid ? updateTemplate : postTemplate;
    return apiAction(permalink, currentScheduleAsTemplate)
      .then((template) => {
        trackEvent("save template successful");
        dispatch(setPostTemplateRequestStatus(RequestStatus.SUCCESS));
        dispatch(receiveTemplates([template as ITemplate]));
      })
      .catch(() => {
        trackEvent("save template failure");
        dispatch(setPostTemplateRequestStatus(RequestStatus.FAILURE));
      });
  };
}

function openTemplate(templateId: string): SchedulingThunkResult<void> {
  return (dispatch, getState): void => {
    const { templates, scheduling } = getState();
    const startDate = scheduling.schedule.startDate.unwrapOr(new Date());
    const templateToOpen = templates.templates[templateId];
    if (templateToOpen) {
      // Carry forward the current schedule but blast away all shifts
      trackEvent("open template");
      const currentScheduleEmpty = emptyScheduleOfShifts(scheduling.schedule);
      const shiftsToOpen = templateToSchedule(templateToOpen, startDate);
      const schedule = mergePeopleIntoSchedule(
        shiftsToOpen.people,
        currentScheduleEmpty
      );
      dispatch(receiveTemplateSchedule(schedule));
    }
  };
}

function deleteTemplate(
  permalink: string,
  uuid: string
): TemplatesThunkResult<void> {
  return (dispatch): Promise<void> => {
    dispatch(setDeleteTemplateRequestStatus(RequestStatus.REQUESTING));
    return requestDeleteTemplate(permalink, uuid)
      .then(() => {
        trackEvent("delete template successful");
        dispatch(setDeleteTemplateRequestStatus(RequestStatus.SUCCESS));
        dispatch(removeTemplate(uuid));
      })
      .catch(() => {
        trackEvent("delete template failure");
        dispatch(setDeleteTemplateRequestStatus(RequestStatus.FAILURE));
      });
  };
}

// API Request Actions
interface ISetRequestStatusAction<T> {
  type: TemplatesTypeKeys.SET_REQUEST_STATUS;
  status: RequestStatus;
  apiRequest: T;
}

function setGetTemplatesRequestStatus(
  status: RequestStatus
): ISetRequestStatusAction<TemplateApiRequests.GET> {
  return {
    type: TemplatesTypeKeys.SET_REQUEST_STATUS,
    apiRequest: TemplateApiRequests.GET,
    status,
  };
}

function setPostTemplateRequestStatus(
  status: RequestStatus
): ISetRequestStatusAction<TemplateApiRequests.POST> {
  return {
    type: TemplatesTypeKeys.SET_REQUEST_STATUS,
    apiRequest: TemplateApiRequests.POST,
    status,
  };
}

function setDeleteTemplateRequestStatus(
  status: RequestStatus
): ISetRequestStatusAction<TemplateApiRequests.DELETE> {
  return {
    type: TemplatesTypeKeys.SET_REQUEST_STATUS,
    apiRequest: TemplateApiRequests.DELETE,
    status,
  };
}

export {
  TemplatesTypeKeys,
  TemplatesActionTypes,
  fetchTemplates,
  saveTemplate,
  openTemplate,
  deleteTemplate,
};
